// MOBILE-COMPATIBLE DepartmentSelector.tsx

import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  RefreshCw, 
  Building2, 
  X, 
  Check,
  Minus,
  AlertCircle
} from "lucide-react";
import {
  listDepartments,
  Department
} from "../../../../service/apiService";
import { useUser } from "../../../../context/UserContext";

interface DepartmentSelectorProps {
  selectedDepartmentName?: string;
  onDepartmentSelected: (departmentName: string) => void;
  onDepartmentRemoved: () => void;
  isEnabled: boolean;
}

const DepartmentSelector: React.FC<DepartmentSelectorProps> = ({
  selectedDepartmentName,
  onDepartmentSelected,
  onDepartmentRemoved,
  isEnabled
}) => {
  const { userId } = useUser();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  const fetchDepartments = async () => {
    if (!userId) {
      console.log("No userId available for fetching departments");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await listDepartments();
      setDepartments(response.departments || []);
    } catch (error: any) {
      console.error("Error fetching departments:", error);
      const errorMessage = error.response?.data?.detail || "Failed to fetch departments";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEnabled && userId) {
      fetchDepartments();
    }
  }, [isEnabled, userId]);

  const filteredDepartments = useMemo(() => {
    return departments.filter(dept =>
      dept.department_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [departments, searchQuery]);

  const handleAddDepartment = (departmentName: string) => {
    onDepartmentSelected(departmentName);
    toast.success(`Added to ${departmentName} department`);
  };

  const handleRemoveDepartment = () => {
    onDepartmentRemoved();
    // toast.success("Removed from department");
  };

  if (!isEnabled) {
    return (
      <div className="text-center text-gray-500 py-6 sm:py-8">
        <Building2 size={28} className="mx-auto mb-2 opacity-50 sm:w-8 sm:h-8" />
        <p className="text-xs sm:text-sm px-4">Enable department assignment to select departments</p>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="text-center text-gray-500 py-6 sm:py-8">
        <Building2 size={28} className="mx-auto mb-2 opacity-50 sm:w-8 sm:h-8" />
        <p className="text-xs sm:text-sm">Loading user information...</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-6 sm:py-8">
        <RefreshCw className="animate-spin text-violet-600 mr-2 w-5 h-5 sm:w-6 sm:h-6" />
        <span className="text-gray-600 text-sm sm:text-base">Loading departments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 py-6 sm:py-8 px-4">
        <AlertCircle size={28} className="mx-auto mb-2 sm:w-8 sm:h-8" />
        <p className="text-xs sm:text-sm mb-3 break-words">{error}</p>
        <button
          onClick={fetchDepartments}
          className="text-violet-600 hover:text-violet-700 text-xs sm:text-sm font-medium px-3 py-1 rounded-md hover:bg-violet-50 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (departments.length === 0) {
    return (
      <div className="text-center text-gray-500 py-6 sm:py-8 px-4">
        <Building2 size={28} className="mx-auto mb-2 opacity-50 sm:w-8 sm:h-8" />
        <p className="text-xs sm:text-sm mb-3">No departments available</p>
        <p className="text-xs text-gray-400">
          Ask your admin to create departments first
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          className="w-full pl-9 pr-3 py-2.5 sm:py-2 border border-gray-200 text-sm text-gray-700 bg-white rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 touch-manipulation"
          placeholder="Search departments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Selected Department */}
      {selectedDepartmentName && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <Building2 className="text-violet-600 flex-shrink-0" size={16} />
              <span className="text-sm font-medium text-violet-800 truncate">
                {selectedDepartmentName}
              </span>
              <span className="text-xs text-violet-600 bg-violet-100 px-2 py-1 rounded-full flex-shrink-0">
                Assigned
              </span>
            </div>
            <button
              onClick={handleRemoveDepartment}
              className="text-violet-600 hover:text-violet-800 p-2 sm:p-1 -m-1 flex-shrink-0 touch-manipulation"
              title="Remove from department"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Available Departments */}
      <div className="space-y-1 max-h-40 sm:max-h-32 overflow-y-auto">
        {filteredDepartments.length === 0 ? (
          <div className="text-center text-gray-500 py-6 sm:py-4">
            <p className="text-xs sm:text-sm">
              {searchQuery ? "No departments found" : "No departments available"}
            </p>
          </div>
        ) : (
          filteredDepartments.map((department) => {
            const isSelected = selectedDepartmentName === department.department_name;
            
            return (
              <div
                key={department.id}
                className={`flex items-center justify-between py-3 sm:py-2 px-3 sm:px-3 rounded-lg border transition-colors ${
                  isSelected
                    ? "bg-violet-50 border-violet-200"
                    : "bg-gray-50 border-gray-200 hover:bg-gray-100 active:bg-gray-200"
                }`}
              >
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <Building2 
                    className={`flex-shrink-0 ${isSelected ? "text-violet-600" : "text-gray-500"}`} 
                    size={16} 
                  />
                  <span className={`text-sm truncate ${
                    isSelected ? "text-violet-800 font-medium" : "text-gray-700"
                  }`}>
                    {department.department_name}
                  </span>
                </div>
                
                {isSelected ? (
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    <Check className="text-violet-600" size={16} />
                    <button
                      onClick={handleRemoveDepartment}
                      className="text-violet-600 hover:text-violet-800 p-2 sm:p-1 -m-1 touch-manipulation"
                      title="Remove from department"
                    >
                      <Minus size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleAddDepartment(department.department_name)}
                    className="text-violet-600 hover:text-violet-800 p-2 sm:p-1 -m-1 flex-shrink-0 touch-manipulation"
                    title="Add to department"
                  >
                    <Plus size={16} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Refresh Button */}
      <div className="pt-3 sm:pt-2 border-t border-gray-200">
        <button
          onClick={fetchDepartments}
          className="w-full text-center text-violet-600 hover:text-violet-700 text-sm font-medium py-2 sm:py-1 flex items-center justify-center space-x-1 touch-manipulation hover:bg-violet-50 rounded-md transition-colors"
        >
          <RefreshCw size={14} />
          <span>Refresh Departments</span>
        </button>
      </div>
    </div>
  );
};

export default DepartmentSelector;